/**
 * The seam between a pane and the field, and the control that moves it.
 *
 * ONE COMPONENT FOR BOTH PANES. The thread column grew a seam first (D57) and
 * the explorations sidebar grew the same one (D104); the pointer half of a seam
 * is a hundred lines of hard-won cases — a captured pointer, a cancel that is
 * not a commit, a second finger, a window losing focus mid-drag — and two
 * copies of it are a drift waiting for the next fix to land in one of them.
 * What differs between the panes is only arithmetic and destination, so those
 * arrive as props: the bounds, the step, where the live width is painted, and
 * what is told once at the end.
 *
 * A REAL SEPARATOR, not a decorated div: `role="separator"` with an orientation
 * and a value, focusable, and driven by ←/→, Home and End. A pane you can only
 * resize by dragging is a pane a keyboard cannot resize at all, and this app has
 * a standing rule against that. The value it reports is the width in pixels
 * against the same bounds the drag is clamped to, so a reader hears the number
 * change and hears it stop at each end.
 *
 * The pointer half carries the scars of the sidebar's own row drags: the
 * pointer is CAPTURED, so a release outside the window still arrives;
 * `pointercancel` is a cancel and NOT a commit, because a gesture the browser
 * took away is not a gesture somebody finished; and `blur` is the belt, for a
 * window that loses focus mid-drag and may never be told the button came up. A
 * cancel puts the pane back where it was.
 *
 * While the drag is live the width goes to `paint` and nowhere else — the
 * caller's custom property, never the store, for the reason `THREAD_W_VAR`
 * gives — and `commit` is told once, at the end.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'

export function PaneSeam({
  width,
  min,
  max,
  step,
  label,
  paint,
  commit,
  stepBy,
  onDragging,
}: {
  /** The committed width — the value of record, and what a cancel returns to. */
  width: number
  min: number
  max: number
  /** One press of ← or →, in pixels. */
  step: number
  /** The separator's accessible name: which pane, and that it resizes it. */
  label: string
  /** Paint a width without telling anybody. The store hears once, at the end. */
  paint: (w: number) => void
  /** The width of record, or null for "no setting" — the double-click's reset. */
  commit: (w: number | null) => void
  /** One step of the keyboard from the committed width, held inside the pane's
   *  own bounds — the caller's arithmetic, so the handle and its tests do it
   *  once. */
  stepBy: (by: number) => number
  /** Told when a drag begins and when it ends, for a caller that has to take a
   *  transition away for the life of the gesture. */
  onDragging?: (live: boolean) => void
}) {
  const [dragging, setDragging] = useState(false)
  const from = useRef<{ x: number; w: number } | null>(null)
  const live = useRef(width)
  const detach = useRef<(() => void) | null>(null)

  const show = (w: number) => {
    live.current = w
    paint(w)
  }

  const stop = () => {
    detach.current?.()
    detach.current = null
    from.current = null
    setDragging(false)
    onDragging?.(false)
  }

  /**
   * A drag that was TAKEN rather than finished: the pane goes back to the width
   * it had, and nothing is committed.
   *
   * ONE COPY, deliberately. Escape had its own — the same four statements
   * written out again — and two copies of "cancel a live drag" is a drift
   * waiting for the next fix to land in one of them. It nearly did: the
   * pointer-id filter below is a change to cancelling, and with two bodies it
   * would have had to be remembered twice on the same afternoon.
   */
  const cancelDrag = () => {
    stop()
    show(width)
  }

  const onDown = (e: ReactPointerEvent) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return
    /* ONE PRESS AT A TIME. A second pointerdown on the seam — a second finger,
       which is an ordinary thing to do — used to overwrite `detach` with the new
       press's teardown, stranding the first press's four window listeners for
       the life of the page. The stranded `up` is the sharp end: it fires on
       every later pointerup anywhere and commits the width its own long-dead
       gesture was holding. The sidebar's row drags refuse a second press for
       exactly this reason; so does this. */
    if (detach.current) return
    // The canvas owns the field's pointers and this handle sits on the boundary
    // between them. Stopped here so a resize can never also be a pan.
    e.stopPropagation()
    e.preventDefault()
    const source = e.currentTarget
    const pointerId = e.pointerId
    try {
      source.setPointerCapture(pointerId)
    } catch {
      /* A pointer already gone. The window listeners still cover it. */
    }
    from.current = { x: e.clientX, w: width }
    live.current = width
    setDragging(true)
    onDragging?.(true)

    /* THE POINTER THAT STARTED THIS, and no other. The listeners are on the
       window, so without this a second finger anywhere on the page — on the
       canvas, on the other pane — drove `dx` off the FIRST finger's start point
       and its release committed whatever that produced. Capture retargets a
       pointer's own events; it says nothing about anybody else's. */
    const mine = (ev: PointerEvent) => ev.pointerId === pointerId

    const move = (ev: PointerEvent) => {
      if (!mine(ev)) return
      const f = from.current
      if (!f) return
      const dx = ev.clientX - f.x
      // Six pixels before anything moves, the same activation the row drags use
      // and for the same reason: a press that is really a click must not nudge
      // the pane. Past it the FULL delta applies, so the pane does not jump by
      // the threshold it just spent.
      if (Math.abs(dx) < 6 && live.current === f.w) return
      show(Math.min(max, Math.max(min, Math.round(f.w + dx))))
    }
    const up = (ev: PointerEvent) => {
      if (!mine(ev)) return
      const settled = live.current
      stop()
      // Committed once. The caller re-reads its store, recomputes the inset and
      // writes the property back from the number of record — so nothing here
      // has to put the property right, and nothing can drift.
      if (settled !== width) commit(settled)
    }
    const cancel = (ev: PointerEvent) => {
      if (!mine(ev)) return
      cancelDrag()
    }
    // No pointer to check: the window losing focus is about the window. See the
    // belt this is, on the header.
    const blurred = () => cancelDrag()
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', cancel)
    window.addEventListener('blur', blurred)
    detach.current = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', cancel)
      window.removeEventListener('blur', blurred)
      try {
        source.releasePointerCapture(pointerId)
      } catch {
        /* Released by the gesture ending, which is the ordinary case. */
      }
    }
  }

  // Escape ends a live drag and puts the pane back, at the layer that owns it —
  // the same one-press-one-layer contract every popover in this app keeps.
  // Through `cancelDrag`, which is the only body this act has.
  useEffect(() => {
    if (!dragging) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      cancelDrag()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  const stepTo = (to: number) => {
    /* A STEP THAT MOVES NOTHING WRITES NOTHING, and that is the same rule the
       pointer path keeps one screen up. It matters most at the ends: → at this
       window's maximum would otherwise store the maximum, and the whole promise
       of the stored width is that it is clamped on READ and never rewritten
       into the slot — a 900 set on a monitor would be destroyed by one arrow
       press on a laptop. An arrow at a bound with nothing stored is the same
       fault the other way: it would pin `null` to a literal and the pane would
       stop following the stage. */
    if (to === width) return
    show(to)
    commit(to)
  }

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      aria-valuemax={max}
      className="paneseam"
      data-dragging={dragging || undefined}
      onPointerDown={onDown}
      // Back to the width nobody asked for. `null` is the setting's ABSENCE
      // rather than today's number, so the pane goes back to its default on
      // whatever screen it is opened on next.
      onDoubleClick={() => commit(null)}
      onKeyDown={(e) => {
        if (e.metaKey || e.ctrlKey || e.altKey) return
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault()
          e.stopPropagation()
          stepTo(stepBy(e.key === 'ArrowRight' ? step : -step))
          return
        }
        if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault()
          e.stopPropagation()
          stepTo(e.key === 'Home' ? min : max)
        }
      }}
    >
      <span aria-hidden className="paneseam__grip" />
    </div>
  )
}
